
// apps/patient/src/components/messages/InlineThreadView.tsx

import { useState, useEffect, useRef } from 'react';
import {
  User,
  AlertCircle,
  Info
} from 'lucide-react';
import { patientApiService as apiService } from '../../services/api';
import { usePatient } from '../../contexts/PatientContext';
import MessageBubble from './MessageBubble';
import MessageComposer from './MessageComposer';
import { cn } from '../../lib/utils';
import { formatDistanceToNow } from 'date-fns';

export default function InlineThreadView({ thread, onRefresh, isPatient = true }) {
  const { patient } = usePatient();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // State
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Load messages immediately when thread opens
  useEffect(() => {
    if (!thread?.id) return;
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.id]);

  // Load messages when thread changes
  useEffect(() => {
    if (!thread?.id) return;

    const handleRefresh = () => {
      fetchMessages();
    };

    (window as any).refreshMessages = handleRefresh; // TS-safe
    window.addEventListener('refreshMessages', handleRefresh);

    return () => {
      delete (window as any).refreshMessages;
      window.removeEventListener('refreshMessages', handleRefresh);
    };
  }, [thread?.id]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messages.length > 0 && !loadingMore) {
      scrollToBottom();
    }
  }, [messages, loadingMore]);

  // Mark as read
  useEffect(() => {
    if (thread?.id && messages.length > 0 && patient?.id) {
      const currentUserParticipant = thread.participants?.find(
        p => p.user_id === patient.id
      );
      const latestMessage = messages[messages.length - 1];

      if (!currentUserParticipant?.last_read_message_id ||
        currentUserParticipant.last_read_message_id !== latestMessage.id) {
        markThreadAsRead();
      }
    }
  }, [thread?.id, messages, patient?.id]);

  // Polling for new messages - I think this is causing thousands of requests and exceeding xano quota
  useEffect(() => {
    if (!thread?.id) return;

    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchMessages();
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [thread?.id]);

  const fetchMessages = async () => {
    if (!thread?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getMessages(thread.id, { limit: 50 });
      setMessages(response.items || []);
      setHasMore(response.items_total > response.items_received);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreMessages = async () => {
    if (loadingMore || !hasMore || messages.length === 0) return;

    setLoadingMore(true);

    try {
      const oldestMessage = messages[0];
      const response = await apiService.getMessages(thread.id, {
        before: oldestMessage.created_at,
        limit: 50
      });

      setMessages([...response.items, ...messages]);
      setHasMore(response.items_total > messages.length + response.items_received);
    } catch (err) {
      console.error('Error loading more messages:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const markThreadAsRead = async () => {
    if (!thread?.id || messages.length === 0 || !patient?.id) return;

    try {
      await apiService.markThreadRead(thread.id);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error marking thread as read:', err);
    }
  };

  const handleSendMessage = async (messageData) => {
    setSending(true);

    try {
      // Add patient sender info
      const fullMessageData = {
        ...messageData,
        sender_id: patient?.id,
        sender_name: `${patient?.first_name} ${patient?.last_name}`,
        sender_type: 'patient'
      };

      // Send message
      const response = await apiService.sendMessage(thread.id, fullMessageData);
      const messageIndex = messages.length;

      (response as any).onAttachmentsUploaded = (attachments: any[]) => {
        setMessages((prev) => {
          const next = prev.slice();
          const target = { ...next[messageIndex] };

          // Normalize attachment fields to what your bubble expects
          const normalized = attachments.map((a: any) => ({
            id: a.id,
            filename: a.filename || a.file_name || a.file_data?.name,
            file_name: a.file_name || a.file_data?.name || a.filename,
            name: a.file_name || a.file_data?.name || a.filename,
            size: a.file_size,
            mime_type: a.mime_type,
            url: a.file_url || a.file_data?.url,
            file_url: a.file_url || a.file_data?.url,
          }));

          target.attachments = normalized;
          target.contains_attachments = true;

          next[messageIndex] = target;
          return next;
        });
      };

      // Add message to local state
      setMessages([...messages, response]);

      // Update thread metadata
      await apiService.updateMessageThread(thread.id, {
        last_message_at: Date.now(),
        last_message_preview: messageData.body.substring(0, 100) + (messageData.body.length > 100 ? '...' : ''),
        last_sender_id: patient?.id,
        last_sender_name: `${patient?.first_name} ${patient?.last_name}`,
        message_count: (thread.message_count || 0) + 1
      });

      // Clear reply state
      setReplyToMessage(null);

      // Refresh thread list
      if (onRefresh) onRefresh();

      // Mark thread as read since we just sent a message
      setTimeout(() => {
        markThreadAsRead();
      }, 100);


      return response;
    } catch (err) {
      console.error('Error sending message:', err);
      throw err;
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentDate = null;

    messages.forEach(message => {
      const messageDate = new Date(message.created_at).toDateString();

      if (messageDate !== currentDate) {
        currentDate = messageDate;
        const date = new Date(message.created_at);
        const today = new Date().toDateString();
        const yesterday = new Date(Date.now() - 86400000).toDateString();

        let relativeDate;
        if (messageDate === today) {
          relativeDate = 'Today';
        } else if (messageDate === yesterday) {
          relativeDate = 'Yesterday';
        } else {
          relativeDate = date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        }

        groups.push({ type: 'date', relativeDate });
      }

      groups.push({ type: 'message', message });
    });

    return groups;
  };

  const groupedMessages = groupMessagesByDate(messages);

  if (loading && messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading messages...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-red-600">
          <AlertCircle className="h-12 w-12 mx-auto mb-2" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Thread Header */}
      <div className="border-b px-4 py-3 bg-gray-50">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {thread.subject || 'Conversation'}
            </h2>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
              <span>Category: {thread.category?.replace('_', ' ')}</span>
              {thread.priority === 'urgent' && (
                <span className="text-red-600 font-medium">• Urgent</span>
              )}
              {thread.status === 'closed' && (
                <span className="text-gray-500">• Closed</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-gray-500">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <>
            {hasMore && (
              <div className="text-center mb-4">
                <button
                  onClick={loadMoreMessages}
                  disabled={loadingMore}
                  className="text-sm text-primary hover:text-primary/80"
                >
                  {loadingMore ? 'Loading...' : 'Load earlier messages'}
                </button>
              </div>
            )}

            <div className="space-y-4">
              {groupedMessages.map((item, index) => {
                if (item.type === 'date') {
                  return (
                    <div key={`date-${index}`} className="flex items-center justify-center my-4">
                      <div className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                        {item.relativeDate}
                      </div>
                    </div>
                  );
                }

                const message = item.message;
                const isOwnMessage = message.sender_id === patient.id;

                return (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isOwnMessage={isOwnMessage}
                    onReply={() => setReplyToMessage(message)}
                    threadId={thread.id}
                    isPatient={true}
                  />
                );
              })}
            </div>
          </>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Composer or Closed Message */}
      {thread.status === 'open' ? (
        <MessageComposer
          threadId={thread.id}
          onSend={handleSendMessage}
          onCancel={() => setReplyToMessage(null)}
          disabled={sending}
          replyToMessage={replyToMessage}
          isPatient={true}
        />
      ) : (
        <div className="border-t px-4 py-3 bg-gray-50">
          <p className="text-sm text-gray-600 text-center">
            This conversation is closed. Contact your provider to reopen it.
          </p>
        </div>
      )}
    </div>
  );
}